import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { MapPin, Phone, Truck } from 'lucide-react';

import { toast } from 'react-toastify';

import {
  useCreateMyOrderNote,
  useCustomerOrderAuditLog,
  useCustomerOrderDetails,
  useListMyOrderNotes,
  useMarkMyOrderNotesSeen,
} from '../../features/customer-orders/api';

import { useCurrentUser } from '../../features/auth/api';

import { formatPickupDeadline } from '../../features/pickup-deadline/pickupDeadline';

import { formatPhone } from '../../utils/formatters';

import { CustomerOrderDocuments } from './CustomerOrderDocuments';

import { CUSTOMER_STATUS_LABELS } from '../../hooks/orders/useCustomerOrderColumns';

import './customerOrder.scss';

type CustomerOrderAddress = {
  id: string;

  label?: string | null;

  fullName?: string | null;

  phone?: string | null;

  line1?: string | null;

  line2?: string | null;

  city?: string | null;

  state?: string | null;

  postalCode?: string | null;

  country?: string | null;

  isPrimary?: boolean;
};

type Props = {
  orderId?: string;

  onClose: () => void;

  addresses?: CustomerOrderAddress[];

  currentDeliveryAddressId?: string | null;

  orderOwnerUserId?: string | null;

  isSaving?: boolean;

  onSave?: (args: {
    deliveryAddressId?: string;

    poNumber?: string | null;
  }) => Promise<void>;
};

const CUSTOMER_EDITABLE_ORDER_STATUSES = new Set(['PENDING', 'APPROVED', 'PREPAY_REQUIRED']);

// Once approved, only the PO number can be changed.
const CUSTOMER_ADDRESS_EDITABLE_ORDER_STATUSES = new Set(['PENDING', 'PREPAY_REQUIRED']);

function formatFullAddress(addr: any) {
  if (!addr) return null;

  const parts = [
    addr.fullName,

    addr.line1,

    addr.line2,

    [addr.city, addr.state].filter(Boolean).join(', '),

    addr.postalCode,

    addr.country,
  ]

    .filter(Boolean)

    .join(', ');

  return parts || null;
}

function formatAddressOption(addr: CustomerOrderAddress) {
  const line2 = addr.line2 ? `, ${addr.line2}` : '';

  const label = addr.label ? `${addr.label} • ` : '';

  const primary = addr.isPrimary ? ' (Primary)' : '';

  return `${label}${addr.line1 ?? ''}${line2}, ${addr.city ?? ''}, ${addr.state ?? ''} ${
    addr.postalCode ?? ''
  }${primary}`;
}

function formatOrderDetailQuantity(value: unknown, unitName?: string | null): string {
  if (value === null || value === undefined || value === '') return 'Not provided';
  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return 'Not provided';
  const formatted = quantity.toLocaleString('en-US', { maximumFractionDigits: 4 });
  const unit = typeof unitName === 'string' ? unitName.trim() : '';
  // The label is "Tons" for ton-based loads; show other unit labels explicitly.
  return /^(tons?|tonnes?)$/i.test(unit) || !unit ? formatted : `${formatted} ${unit}`;
}

function formatDate(value?: string | null) {
  if (!value) return 'Not provided';

  const datePart = value.slice(0, 10);

  const [year, month, day] = datePart.split('-');

  return `${month}/${day}/${year}`;
}

function formatDateTime(value?: string | null) {
  if (!value) return 'Not available';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString('en-US', {
    month: 'short',

    day: 'numeric',

    year: 'numeric',

    hour: 'numeric',

    minute: '2-digit',
  });
}

function creatorLabel(
  log?: {
    actorName?: string | null;

    actorEmail?: string | null;

    actorRole?: string | null;
  } | null
) {
  if (!log) return 'Not available';

  const roleLabel =
    log.actorRole === 'ORG_ADMIN'
      ? 'Org Admin'
      : log.actorRole === 'ORG_OWNER'
        ? 'Org Owner'
        : log.actorRole === 'CUSTOMER' || log.actorRole === 'SUBUSER'
          ? 'Customer'
          : log.actorRole?.replace(/_/g, ' ') || 'Unknown';

  const name = log.actorName?.trim();

  const email = log.actorEmail?.trim();

  if (name && email) {
    return `${name} (${email}) — ${roleLabel}`;
  }

  if (name) {
    return `${name} — ${roleLabel}`;
  }

  if (email) {
    return `${email} — ${roleLabel}`;
  }

  return roleLabel;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="order-detail-row">
      <div className="order-detail-row__label">{label}</div>

      <div className="order-detail-row__value">{value}</div>
    </div>
  );
}

export function CustomerOrderModal({
  orderId,

  onClose,

  addresses = [],

  currentDeliveryAddressId,

  orderOwnerUserId,

  isSaving = false,

  onSave,
}: Props) {
  const queryClient = useQueryClient();

  const { data: apiUser } = useCurrentUser();

  const { data, isLoading, isFetching } = useCustomerOrderDetails(orderId);

  const {
    data: auditData,

    isLoading: isAuditLoading,

    isFetching: isAuditFetching,
  } = useCustomerOrderAuditLog(orderId);

  const {
    data: notesData,

    isLoading: isNotesLoading,

    isFetching: isNotesFetching,
  } = useListMyOrderNotes(orderId || '');

  const createNote = useCreateMyOrderNote();

  const { mutate: markNotesSeen } = useMarkMyOrderNotesSeen();

  const [noteText, setNoteText] = useState('');

  const order = data?.order;

  const item = order?.items?.[0];

  const apiUserObj = apiUser?.user ?? apiUser ?? null;

  const isOtherUsersOrder =
    apiUserObj?.type === 'SUBUSER' &&
    Boolean(orderOwnerUserId) &&
    orderOwnerUserId !== apiUserObj?.firebaseUid;

  const statusCode = item?.status ?? '';
  const canSaveStatus = CUSTOMER_EDITABLE_ORDER_STATUSES.has(statusCode);
  const canEditAddress = CUSTOMER_ADDRESS_EDITABLE_ORDER_STATUSES.has(statusCode);
  const canEditOrder = !isOtherUsersOrder && Boolean(onSave);

  const [deliveryAddressId, setDeliveryAddressId] = useState('');

  const [savedDeliveryAddressId, setSavedDeliveryAddressId] = useState('');

  const [poNumber, setPoNumber] = useState('');

  const [savedPoNumber, setSavedPoNumber] = useState('');

  useEffect(() => {
    if (!orderId) return;

    let nextAddressId = currentDeliveryAddressId ?? '';

    if (addresses.length > 0) {
      const currentAddressStillExists =
        Boolean(currentDeliveryAddressId) &&
        addresses.some((address) => address.id === currentDeliveryAddressId);

      if (!currentAddressStillExists) {
        const primary = addresses.find((address) => address.isPrimary);

        nextAddressId = primary?.id ?? addresses[0]?.id ?? '';
      }
    }

    setDeliveryAddressId(nextAddressId);
    setSavedDeliveryAddressId(nextAddressId);
  }, [orderId, currentDeliveryAddressId, addresses]);

  useEffect(() => {
    const nextPo = order?.poNumber ?? '';

    setPoNumber(nextPo);

    setSavedPoNumber(nextPo);
  }, [orderId, order?.poNumber]);

  /*

   * Opening the consolidated details dialog counts as reading new seller notes.

   * The order list is invalidated afterwards so the red dot on Load # clears.

   */

  const markedOrderIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!orderId || markedOrderIdRef.current === orderId) {
      return;
    }

    markedOrderIdRef.current = orderId;

    markNotesSeen(orderId, {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ['orders'],
          exact: false,
        });
      },
    });
  }, [orderId, markNotesSeen, queryClient]);

  const creationAudit = auditData?.logs?.find((log) => log.action === 'CREATE') ?? null;

  const notes = notesData?.notes ?? [];

  const canSendNote = noteText.trim().length > 0 && !createNote.isPending;

  const addressChanged = canEditAddress && deliveryAddressId !== (savedDeliveryAddressId || '');

  const poChanged = poNumber !== savedPoNumber;

  const hasChanges = addressChanged || poChanged;

  const saveDisabled =
    isSaving ||
    !canEditOrder ||
    !canSaveStatus ||
    !hasChanges ||
    (addresses.length > 0 && canEditAddress && !deliveryAddressId);

  const handleSave = async () => {
    if (saveDisabled || !onSave) {
      return;
    }

    try {
      await onSave({
        deliveryAddressId: canEditAddress ? deliveryAddressId : undefined,

        poNumber: poNumber.trim() ? poNumber.trim() : null,
      });

      setSavedDeliveryAddressId(deliveryAddressId);

      setSavedPoNumber(poNumber.trim());

      void queryClient.invalidateQueries({
        queryKey: ['orders'],

        exact: false,
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to update order.');
    }
  };

  const handleSendNote = async () => {
    const body = noteText.trim();

    if (!orderId || !body) {
      return;
    }

    try {
      await createNote.mutateAsync({
        orderId,

        body,
      });

      setNoteText('');

      void queryClient.invalidateQueries({
        queryKey: ['orders'],

        exact: false,
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to send note.');
    }
  };

  const handleClose = () => onClose();

  if (isLoading || isFetching) {
    return (
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal__backdrop" onClick={handleClose} />

        <div className="modal__panel customer-order-modal">
          <div className="modal__header customer-order-modal__header">
            <div>
              <h3 className="modal__title">Loading order...</h3>

              <div className="muted tiny">Fetching order details...</div>
            </div>

            <button className="btn btn--ghost" onClick={handleClose}>
              Close
            </button>
          </div>

          <div className="modal__body">
            <div className="customer-order-modal__skeleton">
              <div className="skeleton-block" />

              <div className="skeleton-block" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal__backdrop" onClick={handleClose} />

        <div className="modal__panel customer-order-modal">
          <div className="modal__header customer-order-modal__header">
            <div>
              <h3 className="modal__title">Order not found</h3>

              <div className="muted tiny">The requested order could not be loaded.</div>
            </div>

            <button className="btn btn--ghost" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const status = CUSTOMER_STATUS_LABELS[item?.status] || item?.status;

  const orderShort = item?.loadNumber || `#${order.id.slice(-6)}`;

  const localCarrier = item?.carrier;

  const savedAddress =
    addresses.find((address) => address.id === savedDeliveryAddressId) ??
    order.deliveryAddress ??
    null;

  const shipTo = formatFullAddress(savedAddress);

  const auditLoading = isAuditLoading || isAuditFetching;

  const createdAt = creationAudit?.createdAt ?? order.createdAt;

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={handleClose} />

      <div className="modal__panel customer-order-modal">
        <div className="modal__header customer-order-modal__header">
          <div className="customer-order-modal__title-wrap">
            <h3 className="modal__title">
              Load {orderShort} · {item?.product?.name || 'Product'}
            </h3>

            <div className="customer-order-modal__meta">
              <span>Created: {formatDate(order.createdAt)}</span>

              <span className="customer-order-modal__status">{status}</span>
            </div>
          </div>

          <button className="btn btn--ghost" onClick={handleClose}>
            Close
          </button>
        </div>

        <div className="modal__body customer-order-modal__body">
          <div className="customer-order-grid">
            <div className="customer-order-grid__main">
              <section className="customer-card">
                <h4 className="customer-card__title">Shipping</h4>

                <div className="customer-address-card">
                  <div className="customer-address-card__title">Ship to</div>

                  {savedAddress ? (
                    <>
                      <div className="ship-line">
                        <MapPin size={14} className="icon" />

                        <span>{savedAddress.fullName || '—'}</span>
                      </div>

                      {shipTo && <div className="ship-line text">{shipTo}</div>}

                      {savedAddress.phone && (
                        <div className="ship-line">
                          <Phone size={12} className="icon" />

                          <span>{formatPhone(savedAddress.phone)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="muted">No delivery address on file.</div>
                  )}
                </div>
              </section>

              <section className="customer-card">
                <div
                  style={{
                    display: 'flex',

                    alignItems: 'center',

                    justifyContent: 'space-between',

                    gap: 12,

                    marginBottom: 12,
                  }}
                >
                  <div>
                    <h4 className="customer-card__title" style={{ marginBottom: 2 }}>
                      Updates
                    </h4>

                    <div className="muted tiny">
                      Update the delivery address or PO number while the order is editable.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={saveDisabled}
                    onClick={handleSave}
                    title={
                      !canEditOrder
                        ? 'Only the user who created this order can update it.'
                        : !canSaveStatus
                          ? 'Order cannot be updated in its current status.'
                          : !hasChanges
                            ? 'No changes to save.'
                            : ''
                    }
                  >
                    {isSaving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>

                {isOtherUsersOrder && (
                  <div
                    className="muted tiny"
                    style={{
                      marginBottom: 12,

                      padding: '8px 10px',

                      borderRadius: 8,

                      background: '#f8fafc',
                    }}
                  >
                    You can view this order and its notes, but only the subuser who created it can
                    change the address or PO number.
                  </div>
                )}

                <div
                  style={{
                    display: 'grid',

                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',

                    gap: 12,
                  }}
                >
                  <div className="form-control" style={{ marginBottom: 0 }}>
                    <label>Delivery address</label>

                    {!addresses.length ? (
                      <div className="muted">You don’t have any saved addresses yet.</div>
                    ) : (
                      <>
                        <select
                          className="select-lg"
                          value={deliveryAddressId}
                          onChange={(event) => setDeliveryAddressId(event.target.value)}
                          disabled={isSaving || !canEditOrder || !canSaveStatus || !canEditAddress}
                        >
                          {addresses.map((address) => (
                            <option key={address.id} value={address.id}>
                              {address.fullName || 'Address'} · {formatAddressOption(address)}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>

                  <div className="form-control" style={{ marginBottom: 0 }}>
                    <label>PO number</label>

                    <input
                      className="select-lg"
                      value={poNumber}
                      onChange={(event) => setPoNumber(event.target.value)}
                      placeholder="e.g. PO-10491"
                      disabled={isSaving || !canEditOrder || !canSaveStatus}
                    />
                  </div>
                </div>
              </section>

              <section className="customer-card">
                <h4 className="customer-card__title">Order details</h4>

                <div className="order-detail-grid">
                  <DetailRow
                    label="Fulfilment Method"
                    value={item?.orgLoadOptionNameSnapshot || 'Not selected'}
                  />

                  <DetailRow
                    label={
                      /^(tons?|tonnes?)$/i.test(item?.unitNameSnapshot ?? '') ? 'Tons' : 'Quantity'
                    }
                    value={formatOrderDetailQuantity(item?.quantity, item?.unitNameSnapshot)}
                  />
                  <DetailRow
                    label="Ticket Number"
                    value={item?.ticketNumber?.trim() || 'Not provided'}
                  />

                  <DetailRow label="PO Number" value={savedPoNumber || 'Not provided'} />

                  <DetailRow label="Pickup Date" value={formatDate(item?.pickupDate)} />

                  {item?.status === 'READY_FOR_PICKUP' && item?.pickupDate && (
                    <DetailRow
                      label="Pickup Deadline"
                      value={formatPickupDeadline(item.pickupDate)}
                    />
                  )}

                  <DetailRow label="Delivery Date" value={formatDate(item?.deliveryDate)} />

                  {item?.cancellationReason && (
                    <DetailRow label="Cancellation reason" value={item.cancellationReason} />
                  )}

                  {item?.rejectionReason && (
                    <DetailRow label="Rejection reason" value={item.rejectionReason} />
                  )}
                </div>
              </section>

              <CustomerOrderDocuments orderId={order.id} />
            </div>

            <aside className="customer-order-grid__side">
              <section className="customer-card">
                <h4 className="customer-card__title">Carrier</h4>

                {localCarrier ? (
                  <div className="carrier-detail-list">
                    <DetailRow label="Company" value={localCarrier.companyName || 'Not provided'} />
                  </div>
                ) : (
                  <div className="carrier-empty">
                    <div className="carrier-empty__text">
                      <Truck size={20} className="carrier-empty__icon" />

                      <span>No carrier assigned</span>
                    </div>
                  </div>
                )}
              </section>

              <section className="customer-card">
                <h4 className="customer-card__title">Audit log</h4>

                {auditLoading ? (
                  <div className="muted tiny">Loading audit log...</div>
                ) : (
                  <div className="carrier-detail-list">
                    <DetailRow label="Created by" value={creatorLabel(creationAudit)} />

                    <DetailRow label="Created at" value={formatDateTime(createdAt)} />
                  </div>
                )}
              </section>
            </aside>
          </div>

          <section className="customer-card" style={{ marginTop: 16 }}>
            <div
              style={{
                display: 'flex',

                alignItems: 'center',

                justifyContent: 'space-between',

                gap: 12,

                marginBottom: 12,
              }}
            >
              <div>
                <h4 className="customer-card__title" style={{ marginBottom: 2 }}>
                  Notes
                </h4>

                <div className="muted tiny">
                  Messages here are shared with your DeFeo Materials team.
                </div>
              </div>

              {isNotesFetching && !isNotesLoading && (
                <span className="muted tiny">Refreshing…</span>
              )}
            </div>

            <div
              style={{
                display: 'grid',

                gridTemplateColumns: 'minmax(0, 1fr) auto',

                gap: 10,

                alignItems: 'end',

                marginBottom: 14,
              }}
            >
              <div className="form-control" style={{ marginBottom: 0 }}>
                <label>Add a note</label>

                <textarea
                  value={noteText}
                  onChange={(event) => setNoteText(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Type a message…"
                  style={{
                    width: '100%',

                    resize: 'vertical',

                    minHeight: 82,

                    border: '1px solid #e5e7eb',

                    borderRadius: 10,

                    padding: 10,

                    font: 'inherit',
                  }}
                />
              </div>

              <button
                type="button"
                className="btn btn--primary"
                onClick={handleSendNote}
                disabled={!canSendNote}
                style={{ minWidth: 110 }}
              >
                {createNote.isPending ? 'Sending…' : 'Send note'}
              </button>
            </div>

            {isNotesLoading ? (
              <div className="skeleton" style={{ height: 160 }} />
            ) : notes.length === 0 ? (
              <div className="muted">No notes yet.</div>
            ) : (
              <div
                style={{
                  display: 'flex',

                  flexDirection: 'column',

                  gap: 10,

                  maxHeight: 340,

                  overflowY: 'auto',

                  paddingRight: 4,
                }}
              >
                {notes.map((note: any) => {
                  const mine = note.authorType === 'CUSTOMER';

                  return (
                    <div
                      key={note.id}
                      style={{
                        border: '1px solid #e5e7eb',

                        borderRadius: 12,

                        padding: 10,

                        background: mine ? '#eff6ff' : '#f9fafb',

                        alignSelf: mine ? 'flex-end' : 'flex-start',

                        width: 'min(90%, 720px)',
                      }}
                    >
                      <div className="muted tiny" style={{ marginBottom: 6 }}>
                        {mine ? 'You' : 'DeFeo Materials'} •{' '}
                        {new Date(note.createdAt).toLocaleString()}
                      </div>

                      <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {note.body}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
